import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import Sidebar from "../../../components/Sidebar";
import ProfileInfoController from "../../../controller/profileInfoController";
import {
  UserDocumentType,
  UserDocumentSubType,
  KycStatus,
} from "../../../controller/profileInfoController";
import { getHomePageInfos, fetchDocumentImage } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import dropdownOptions from "../../../data/dropdownOptions.json";
import countries from "world-countries";
import "../../css/ProfileInformation.css";
import { undoSignatureStroke } from "../../../utils/signatureCanvas";

const {
  clientTypeOptions,
  internalTypeOptions,
  profileGenderOptions,
  profileMaritalOptions,
  residenceOptions,
  identityDocTypeOptions,
  addressDocTypeOptions,
} = dropdownOptions;

const ONE_FACE_DOC_TYPES = [2, 5];

const ADDRESS_PARTS = [
  ["neighborhood", "Neighborhood"],
  ["street", "Street"],
  ["buildingName", "Building Name"],
  ["besideTo", "Beside To"],
  ["floor", "Floor"],
  ["block", "Block"],
  ["buildingNumber", "Building Number"],
  ["streetNumber", "Street Number"],
  ["landlinePhone", "Landline Phone"],
  ["boxNumber", "Box Number"],
];

function parseStructuredAddress(raw) {
  if (!raw || typeof raw !== "string") return null;
  const found = {};
  let hits = 0;
  raw.split(/\r?\n/).forEach((line) => {
    const splitAt = line.indexOf(": ");
    if (splitAt === -1) return;
    const label = line.slice(0, splitAt).trim().toLowerCase();
    const value = line.slice(splitAt + 2).trim();
    const match = ADDRESS_PARTS.find(([, partLabel]) => partLabel.toLowerCase() === label);
    if (match && value) {
      found[match[0]] = value;
      hits += 1;
    }
  });
  return hits > 0 ? found : null;
}

// Image loading is now handled by fetchDocumentImage from apiClient

/**
 * Compress/resize an image File before upload so the request stays well under the
 * backend's POST size limit (mirrors the mobile apps' ImagePicker imageQuality).
 * Non-image files (e.g. video signatures) are returned unchanged.
 */
async function compressImage(file, { maxSize = 1280, quality = 0.6 } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = dataUrl;
    });

    let { width, height } = img;
    if (width > maxSize || height > maxSize) {
      if (width >= height) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // Flatten transparency onto white so JPEG output looks correct (e.g. signatures).
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob || blob.size >= file.size) return file;

    const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
  } catch (_) {
    return file;
  }
}

function findOption(options, value) {
  if (value == null) return null;
  return (
    options.find(
      (o) =>
        o.value === value ||
        o.value === Number(value) ||
        String(o.value) === String(value)
    ) || null
  );
}

export default function ProfileInformation() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [activeSection, setActiveSection] = useState("client");

  const [expandedSections, setExpandedSections] = useState({
    client: true,
    personal: false,
    address: false,
    documents: false,
  });
  const [activeDocTab, setActiveDocTab] = useState("selfie");

  // Client Info
  const [clientType, setClientType] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [nickName, setNickName] = useState("");

  // Personal Info
  const [firstName, setFirstName] = useState("");
  const [fatherName, setFatherName] = useState("");
  const [lastName, setLastName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [gender, setGender] = useState(null);
  const [profession, setProfession] = useState("");
  const [maritalStatus, setMaritalStatus] = useState(null);
  const [spouseName, setSpouseName] = useState("");
  const [spouseProfession, setSpouseProfession] = useState("");
  const [isPep, setIsPep] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [placeOfBirth, setPlaceOfBirth] = useState("");

  // Address Info
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [address, setAddress] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [street, setStreet] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [besideTo, setBesideTo] = useState("");
  const [floor, setFloor] = useState("");
  const [block, setBlock] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [landlinePhone, setLandlinePhone] = useState("");
  const [boxNumber, setBoxNumber] = useState("");
  const [addressEntryMode, setAddressEntryMode] = useState("details");
  const [workAddress, setWorkAddress] = useState("");
  const [city, setCity] = useState("");
  const [residence, setResidence] = useState(null);
  const [country, setCountry] = useState("");
  const [nationality, setNationality] = useState("");

  // Documents
  const [identityDocType, setIdentityDocType] = useState(null);
  const [addressDocType, setAddressDocType] = useState(null);
  const [identificationNumber, setIdentificationNumber] = useState("");
  const [selfieFile, setSelfieFile] = useState(null);
  const [frontIdFile, setFrontIdFile] = useState(null);
  const [backIdFile, setBackIdFile] = useState(null);
  const [addressFile, setAddressFile] = useState(null);
  const [signatureVideoFile, setSignatureVideoFile] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const signatureCanvasRef = useRef(null);

  // Existing document file paths from server
  const [existingDocs, setExistingDocs] = useState({});
  // Resolved blob URLs for display
  const [resolvedImages, setResolvedImages] = useState({});
  // Track which existing images were deleted
  const [deletedDocs, setDeletedDocs] = useState({});

  // Raw data refs
  const rawInfoRef = useRef({});
  const rawDocsRef = useRef([]);

  const kycStatus = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return (
      info.IS_KYC_VERIFIED ?? info.isKycVerified ?? info.is_kyc_verified ?? 0
    );
  }, [profileData]);

  const canEdit =
    kycStatus !== KycStatus.VERIFIED &&
    kycStatus !== KycStatus.VERIFIED_NOT_CHECKED;

  const allowEditing = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return info.ALLOW_EDITING ?? info.allowEditing ?? info.allow_editing ?? true;
  }, [profileData]);

  const kycComments = useMemo(() => {
    const info =
      profileData?.raw?.user_information ||
      profileData?.raw?.USER_INFORMATION ||
      {};
    return info.COMMENTS ?? info.comments ?? "";
  }, [profileData]);

  const kycStatusText = useMemo(() => {
    if (
      kycStatus === KycStatus.VERIFIED ||
      kycStatus === KycStatus.VERIFIED_NOT_CHECKED
    )
      return t("verified", { ns: "mobile" });
    return t("not_verified", { ns: "mobile" });
  }, [kycStatus, t]);

  const countryOptions = useMemo(() => {
    return countries
      .filter((c) => c.cca2 !== "IL")
      .map((c) => ({
        value: c.cca2,
        label: `${c.name.common} (${c.cca2})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  useEffect(() => {
    if (success) {
      const t = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(t);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(t);
    }
  }, [error]);

  const populateFields = useCallback(
    (data) => {
      const rawInfo =
        data.raw?.user_information || data.raw?.USER_INFORMATION || {};
      rawInfoRef.current = rawInfo;

      setClientType(findOption(clientTypeOptions, data.type));
      setAccountType(findOption(internalTypeOptions, data.internalType));
      setNickName(data.nickName || "");

      setFirstName(data.firstName || "");
      setFatherName(data.fatherName || "");
      setLastName(data.lastName || "");
      setMotherName(data.motherName || "");
      setGender(findOption(profileGenderOptions, data.gender));
      setProfession(data.profession || "");
      setMaritalStatus(findOption(profileMaritalOptions, data.marital));
      setSpouseName(data.spouseName || "");
      setSpouseProfession(data.spouseProfession || "");
      setIsPep(data.isPep === true || data.isPep === 1);
      setBirthDate(data.birthDay ? data.birthDay.split("T")[0] : "");
      setPlaceOfBirth(data.placeOfBirth || "");

      setState(data.state || "");
      setZipCode(data.zipCode || "");
      setAddress(data.address || "");
      const addressParts = parseStructuredAddress(data.address);
      setNeighborhood(addressParts?.neighborhood || "");
      setStreet(addressParts?.street || "");
      setBuildingName(addressParts?.buildingName || "");
      setBesideTo(addressParts?.besideTo || "");
      setFloor(addressParts?.floor || "");
      setBlock(addressParts?.block || "");
      setBuildingNumber(addressParts?.buildingNumber || "");
      setStreetNumber(addressParts?.streetNumber || "");
      setLandlinePhone(addressParts?.landlinePhone || "");
      setBoxNumber(addressParts?.boxNumber || "");
      setWorkAddress(data.workAddress || "");
      setCity(data.city || "");
      setResidence(findOption(residenceOptions, data.residence));
      setCountry(data.country || "");
      setNationality(data.nationality || "");

      setIdentificationNumber(data.identificationNumber || "");

      // Extract document info
      const docInfo = ProfileInfoController.extractDocumentUrls(data.raw);
      setExistingDocs(docInfo);
      rawDocsRef.current = docInfo.rawDocuments || [];

      if (docInfo.identityDocSubType != null) {
        setIdentityDocType(
          findOption(identityDocTypeOptions, docInfo.identityDocSubType)
        );
      }
      if (docInfo.addressDocSubType != null) {
        setAddressDocType(
          findOption(addressDocTypeOptions, docInfo.addressDocSubType)
        );
      }
      const hasAddressProof = Boolean(docInfo.address || docInfo.addressDocSubType != null);
      setAddressEntryMode(addressParts ? "details" : hasAddressProof ? "proof" : "details");

      setDeletedDocs({});
      setShowSignaturePad(false);
    },
    []
  );

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      let userDataFromStorage = null;
      // On a forced refresh (e.g. right after saving) bypass the session cache so
      // the header (name/status) reflects the just-saved values too.
      if (!forceRefresh) {
        try {
          const storedUserData = sessionStorage.getItem("userData");
          const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              userDataFromStorage = JSON.parse(storedUserData);
              setUser(userDataFromStorage);
            }
          }
        } catch (_) {}
      }

      if (!userDataFromStorage) {
        try {
          const storedUserData = sessionStorage.getItem("userData");
          if (storedUserData) {
            userDataFromStorage = JSON.parse(storedUserData);
            setUser(userDataFromStorage);
          }
        } catch (_) {}
      }

      if (!userDataFromStorage) {
        const userResponse = await getHomePageInfos({ forceRefresh });
        const userData = userResponse?.decryptedData || userResponse?.data;
        if (
          userData &&
          userData.CODE === AppConstants.responseOk &&
          userData.DATA
        ) {
          setUser(userData.DATA);
          try {
            sessionStorage.setItem("userData", JSON.stringify(userData.DATA));
            sessionStorage.setItem(
              "userDataTimestamp",
              Date.now().toString()
            );
          } catch (_) {}
        }
      }

      const result = await ProfileInfoController.getProfileInfo({ forceRefresh });
      if (result.success) {
        setProfileData(result.data);
        populateFields(result.data);
        const syncedUser = ProfileInfoController.syncCachedUserProfile({
          nickName: result.data.nickName,
          firstName: result.data.firstName,
          lastName: result.data.lastName,
        });
        if (syncedUser) {
          setUser(syncedUser);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message || "Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [populateFields]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobileView) return;
    setExpandedSections({
      client: activeSection === "client",
      personal: activeSection === "personal",
      address: activeSection === "address",
      documents: activeSection === "documents",
    });
  }, [activeSection, isMobileView]);

  useEffect(() => {
    let cancelled = false;
    const keys = ["frontId", "backId", "selfie", "profile", "address", "signature"];
    const toLoad = keys.filter((k) => existingDocs[k]);
    if (toLoad.length === 0) {
      setResolvedImages({});
      return;
    }

    (async () => {
      const resolved = {};
      await Promise.all(
        toLoad.map(async (key) => {
          const blobUrl = await fetchDocumentImage(existingDocs[key]);
          if (!cancelled && blobUrl) resolved[key] = blobUrl;
        })
      );
      if (!cancelled) setResolvedImages(resolved);
    })();

    return () => { cancelled = true; };
  }, [existingDocs]);

  // ---------- TOGGLE SECTION ----------
  const toggleSection = (key) => {
    if (!isMobileView) {
      setActiveSection(key);
      return;
    }
    setExpandedSections((prev) => {
      const isOpen = prev[key];
      const allClosed = { client: false, personal: false, address: false, documents: false };
      return isOpen ? allClosed : { ...allClosed, [key]: true };
    });
  };

  const composeStructuredAddress = () => {
    const values = {
      neighborhood,
      street,
      buildingName,
      besideTo,
      floor,
      block,
      buildingNumber,
      streetNumber,
      landlinePhone,
      boxNumber,
    };
    return ADDRESS_PARTS
      .map(([key, label]) => {
        const value = (values[key] || "").trim();
        return value ? `${label}: ${value}` : "";
      })
      .filter(Boolean)
      .join("\n");
  };

  // ---------- SAVE ----------
  const handleSave = async () => {
    // Validate required fields
    if (!firstName.trim()) return setError("First name is required");
    if (!lastName.trim()) return setError("Last name is required");
    if (!fatherName.trim()) return setError("Father name is required");
    if (!motherName.trim()) return setError("Mother name is required");
    if (!gender) return setError("Gender is required");
    if (!maritalStatus) return setError("Marital status is required");
    if (maritalStatus?.value === 1) {
      if (!spouseName.trim()) return setError("Spouse name is required");
      if (!spouseProfession.trim())
        return setError("Spouse profession is required");
    }
    if (!birthDate) return setError("Birth date is required");
    if (!placeOfBirth.trim()) return setError("Place of birth is required");
    if (addressEntryMode === "proof") {
      if (!addressDocType) return setError("Please select an address document type");
    } else {
      if (!state.trim()) return setError("State is required");
      if (!zipCode.trim()) return setError("Zip code is required");
      if (!city.trim()) return setError("City is required");
      if (neighborhood.trim().length < 2) return setError("Neighborhood is required");
      if (street.trim().length < 2) return setError("Street is required");
      if (buildingName.trim().length < 2) return setError("Building name is required");
      if (besideTo.trim().length < 2) return setError("Beside to is required");
      if (!floor.trim()) return setError("Floor is required");
      if (!landlinePhone.trim()) return setError("Landline phone is required");
      if (!workAddress.trim()) return setError("Work address is required");
      if (!residence) return setError("Residence status is required");
      if (!country.trim()) return setError("Country is required");
      if (!nationality.trim()) return setError("Nationality is required");
    }
    if (!identityDocType) return setError("Please select an identity document type");

    // Validate document images exist (either new upload or existing not deleted)
    const hasFrontId =
      frontIdFile || (existingDocs.frontId && !deletedDocs.frontId);
    if (!hasFrontId) return setError("Front ID image is required");

    const needsBackId = !ONE_FACE_DOC_TYPES.includes(identityDocType?.value);
    if (needsBackId) {
      const hasBackId =
        backIdFile || (existingDocs.backId && !deletedDocs.backId);
      if (!hasBackId) return setError("Back ID image is required");
    }

    const hasSelfie =
      selfieFile || (existingDocs.selfie && !deletedDocs.selfie);
    if (!hasSelfie) return setError("Selfie image is required");

    if (addressEntryMode === "proof") {
      const hasAddress =
        addressFile || (existingDocs.address && !deletedDocs.address);
      if (!hasAddress) return setError("Address document is required");
    }

    // Signature: either pad drawn, existing image, or new upload
    let signatureFile = null;
    if (showSignaturePad) {
      const canvas = signatureCanvasRef.current;
      if (!canvas || canvas.isEmpty()) {
        return setError("Please draw your signature");
      }
      const dataUrl =
        typeof canvas.toDataURL === "function"
          ? canvas.toDataURL("image/png")
          : canvas.getCanvas().toDataURL("image/png");
      const resp = await fetch(dataUrl);
      const blob = await resp.blob();
      signatureFile = new File([blob], "signature.png", {
        type: "image/png",
      });
    }

    const hasSignature =
      signatureFile ||
      (existingDocs.signature && !deletedDocs.signature && !showSignaturePad);
    if (!hasSignature) return setError("Signature is required");

    try {
      setSaving(true);
      setError(null);

      const raw = rawInfoRef.current;
      const userInformation = {
        USER_ID: profileData?.id ?? raw.USER_ID,
        F_NAME: firstName.trim(),
        L_NAME: lastName.trim(),
        FATHER_NAME: fatherName.trim(),
        MOTHER_NAME: motherName.trim(),
        PLACE_OF_BIRTH: placeOfBirth.trim(),
        PROFESSION: profession.trim(),
        SPOUSE_NAME: spouseName.trim() || null,
        SPOUSE_PROFESSION: spouseProfession.trim() || null,
        BIRTH_DAY: birthDate || null,
        NATIONALITY: nationality.trim(),
        GENDER: gender?.value ?? 0,
        MARITAL: maritalStatus?.value ?? null,
        BENEFICAL_RIGHT: raw.BENEFICAL_RIGHT ?? null,
        ADDRESS: composeStructuredAddress() || address.trim(),
        WORK_ADDRESS: workAddress.trim(),
        RESIDENCE: residence?.value ?? null,
        COUNTRY: country.trim(),
        CITY: city.trim(),
        STATE: state.trim(),
        ZIP_CODE: zipCode.trim(),
        PHONE: raw.PHONE || "",
        EMAIL: raw.EMAIL || "",
        IDENTIFICATION_NUMBER: identificationNumber.trim(),
        RISK_NOTE: raw.RISK_NOTE ?? null,
        RISK_STATUS: raw.RISK_STATUS ?? 0,
        IS_PEP: isPep ? 1 : 0,
        INTERNAL_TYPE: raw.INTERNAL_TYPE ?? null,
        DEFAULT_LEVERAGE: raw.DEFAULT_LEVERAGE ?? null,
        REFERAL_BROKER: raw.REFERAL_BROKER ?? null,
        REGISTER_NUMBER: raw.REGISTER_NUMBER || "",
        REGISTER_PLACE: raw.REGISTER_PLACE || "",
        OTHER_NATIONALITY: raw.OTHER_NATIONALITY || "",
      };

      // Compress images before upload (mobile apps use imageQuality: 10) so the
      // multipart POST stays under PHP's post_max_size limit (8 MB locally).
      const [
        compressedAddress,
        compressedFront,
        compressedBack,
        compressedSelfie,
        compressedSignature,
      ] = await Promise.all([
        addressFile ? compressImage(addressFile) : null,
        frontIdFile ? compressImage(frontIdFile) : null,
        backIdFile ? compressImage(backIdFile) : null,
        selfieFile ? compressImage(selfieFile) : null,
        signatureFile ? compressImage(signatureFile) : null,
      ]);

      // Build image files map — keys must match Flutter's backend expectations
      const imageFiles = {};
      if (compressedAddress) imageFiles["ADDRESS_IMAGE"] = compressedAddress;
      if (compressedFront) imageFiles["IDENTITY_FT_IMAGE"] = compressedFront;
      if (compressedBack) imageFiles["IDENTITY_BK_IMAGE"] = compressedBack;
      if (compressedSelfie) imageFiles["SELFIE"] = compressedSelfie;
      if (signatureVideoFile)
        imageFiles["SIGNATURE_FT_VIDEO"] = signatureVideoFile;
      if (compressedSignature) imageFiles["SIGNATURE_FT_IMAGE"] = compressedSignature;

      const DT = UserDocumentType;
      const DF = {
        FRONT_IMAGE: 0,
        BACK_IMAGE: 1,
        ADDRESS_IMAGE: 2,
        SELFIE: 3,
        PROFILE: 4,
      };

      const imagesToUpdate = {
        [DT.PROOF_OF_IDENTITY]: {
          [DF.FRONT_IMAGE]: frontIdFile ? "1" : "0",
          [DF.BACK_IMAGE]: backIdFile ? "1" : "0",
        },
        [DT.PROOF_OF_ADDRESS]: {
          [DF.ADDRESS_IMAGE]: addressEntryMode === "proof" && addressFile ? "1" : "0",
        },
        [DT.SELFIE]: {
          [DF.SELFIE]: selfieFile ? "1" : "0",
        },
        [DT.PROFILE]: {
          [DF.PROFILE]: "0",
        },
        [DT.PROOF_OF_SIGNATURE]: {
          [DF.FRONT_IMAGE]: signatureFile ? "1" : "0",
        },
        [DT.PROOF_OF_VIDEO_SIGNATURE]: {
          [DF.FRONT_IMAGE]: signatureVideoFile ? "1" : "0",
        },
      };

      const imagesToDelete = {
        [DT.PROOF_OF_ADDRESS]: {
          [DF.ADDRESS_IMAGE]:
            addressEntryMode === "proof" && deletedDocs.address && !addressFile ? "1" : "0",
        },
        [DT.PROOF_OF_IDENTITY]: {
          [DF.FRONT_IMAGE]:
            deletedDocs.frontId && !frontIdFile ? "1" : "0",
          [DF.BACK_IMAGE]:
            deletedDocs.backId && !backIdFile ? "1" : "0",
        },
        [DT.SELFIE]: {
          [DF.SELFIE]: deletedDocs.selfie && !selfieFile ? "1" : "0",
        },
        [DT.PROFILE]: {
          [DF.PROFILE]: "0",
        },
        [DT.PROOF_OF_SIGNATURE]: {
          [DF.FRONT_IMAGE]:
            showSignaturePad && !signatureFile ? "1" : "0",
        },
        [DT.PROOF_OF_VIDEO_SIGNATURE]: {
          [DF.FRONT_IMAGE]: "0",
        },
      };

      const selectedDocSubTypes = {
        [DT.PROOF_OF_IDENTITY]: identityDocType?.value,
        [DT.PROOF_OF_ADDRESS]: addressEntryMode === "proof" ? addressDocType?.value : null,
        [DT.PROFILE]: UserDocumentSubType.PROFILE,
        [DT.SELFIE]: UserDocumentSubType.SELFIE,
        [DT.PROOF_OF_SIGNATURE]: UserDocumentSubType.SIGNATURE,
        [DT.PROOF_OF_VIDEO_SIGNATURE]: UserDocumentSubType.SIGNATURE_VIDEO,
      };

      const result = await ProfileInfoController.updateUserProfile({
        userId: profileData?.id,
        userInformation,
        nickName: nickName.trim(),
        userType: clientType?.value,
        userDocuments: rawDocsRef.current,
        selectedDocSubTypes,
        imageFiles,
        imagesToUpdate,
        imagesToDelete,
      });

      if (result.success) {
        const syncedUser = ProfileInfoController.syncCachedUserProfile({
          nickName: nickName.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
        if (syncedUser) {
          setUser(syncedUser);
        }

        setSuccess("Profile updated successfully!");
        setIsEditMode(false);
        setShowSignaturePad(false);
        // Clear new file selections
        setSelfieFile(null);
        setFrontIdFile(null);
        setBackIdFile(null);
        setAddressFile(null);
        setSignatureVideoFile(null);
        // Re-fetch data to reflect updated state (bypass caches so the just-saved
        // values render immediately without a manual browser refresh)
        await fetchData(true);
      } else {
        setError(result.error || "Failed to update profile");
      }
    } catch (err) {
      setError(err.message || "An error occurred while saving");
    } finally {
      setSaving(false);
    }
  };

  // ---------- INPUT HELPERS ----------
  const renderField = (labelKey, icon, value, onChange, opts = {}) => {
    const { type = "text", placeholderKey, disabled: forceDisabled } = opts;
    const isDisabled = forceDisabled !== undefined ? forceDisabled : !isEditMode || !canEdit;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">{t(labelKey, { ns: "mobile" })}</label>
        <div className="profile-input-wrapper">
          <span className="profile-input-icon">
            <i className={`fa-solid ${icon}`}></i>
          </span>
          <CustomInput
            type={type}
            inputMode={type === "number" ? "numeric" : undefined}
            placeholder={placeholderKey ? t(placeholderKey, { ns: "mobile" }) : t(labelKey, { ns: "mobile" })}
            value={value}
            onChange={(e) => {
              const nextValue = type === "number" ? e.target.value.replace(/\D/g, "") : e.target.value;
              onChange(nextValue);
            }}
            className="profile-input"
            disabled={isDisabled || saving}
            readOnly={isDisabled}
          />
        </div>
      </div>
    );
  };

  const renderDropdown = (labelKey, options, value, onChange, opts = {}) => {
    const { disabled: forceDisabled } = opts;
    const isDisabled = forceDisabled !== undefined ? forceDisabled : !isEditMode || !canEdit;
    return (
      <div className="profile-form-group">
        <label className="profile-form-label">{t(labelKey, { ns: "mobile" })}</label>
        <CustomDropdown
          options={options}
          value={value}
          onChange={(v) => onChange(v)}
          placeholder={t(labelKey, { ns: "mobile" })}
          className="profile-dropdown"
          disabled={isDisabled || saving}
        />
      </div>
    );
  };

  const renderDocImage = (label, docKey, file, setFile, fieldKey, accept = "image/*") => {
    const displayUrl = resolvedImages[docKey];
    const isDeleted = deletedDocs[docKey];
    const hasExisting = existingDocs[docKey] && displayUrl && !isDeleted;
    const editEnabled = isEditMode && canEdit;

    return (
      <div className="profile-upload-area">
        <label className="profile-form-label">{label}</label>
        <div
          className="profile-upload-box"
          onClick={() => {
            if (editEnabled)
              document.getElementById(`file-${fieldKey}`)?.click();
          }}
          style={{ cursor: editEnabled ? "pointer" : "default" }}
        >
          {file ? (
            <div className="profile-upload-preview">
              {file.type?.startsWith("image/") ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={label}
                  className="profile-upload-thumb"
                />
              ) : (
                <span className="profile-upload-filename">{file.name}</span>
              )}
              {editEnabled && (
                <button
                  type="button"
                  className="profile-upload-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          ) : hasExisting ? (
            <div className="profile-upload-preview">
              <img
                src={displayUrl}
                alt={label}
                className="profile-upload-thumb"
              />
              {editEnabled && (
                <button
                  type="button"
                  className="profile-upload-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletedDocs((prev) => ({
                      ...prev,
                      [docKey]: true,
                    }));
                  }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          ) : (
            <div className="profile-upload-placeholder">
              <i className="fa-solid fa-cloud-arrow-up"></i>
              <span>{editEnabled ? "Click to upload" : "No image"}</span>
            </div>
          )}
          {editEnabled && (
            <input
              id={`file-${fieldKey}`}
              type="file"
              accept={accept}
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  setFile(e.target.files[0]);
                  setDeletedDocs((prev) => ({ ...prev, [docKey]: false }));
                }
              }}
            />
          )}
        </div>
      </div>
    );
  };

  // ---------- SECTIONS ----------
  const renderSectionHeader = (title, sectionKey) => (
    <div
      className="profile-section-header"
      onClick={() => toggleSection(sectionKey)}
    >
      <h3 className="profile-section-title profile-section-title--collapsible">
        {title}
      </h3>
      <i
        className={`fa-solid ${
          expandedSections[sectionKey] ? "fa-chevron-up" : "fa-chevron-down"
        } profile-section-chevron`}
      ></i>
    </div>
  );

  const renderClientSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("client_information", { ns: "mobile" }), "client")}
      {expandedSections.client && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderDropdown(
              "client_type",
              clientTypeOptions,
              clientType,
              setClientType,
              { disabled: true }
            )}
            {renderDropdown(
              "account_type",
              internalTypeOptions,
              accountType,
              setAccountType,
              { disabled: true }
            )}
          </div>
          <div className="profile-form-row">
            {renderField(
              "nickname",
              "fa-id-badge",
              nickName,
              setNickName
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderPersonalSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("personal_information", { ns: "mobile" }), "personal")}
      {expandedSections.personal && (
        <div className="profile-form">
          <div className="profile-form-row">
            {renderField("first_name", "fa-id-badge", firstName, setFirstName)}
            {renderField("father_name", "fa-user", fatherName, setFatherName)}
          </div>
          <div className="profile-form-row">
            {renderField("last_name", "fa-id-badge", lastName, setLastName)}
            {renderField("mother_name", "fa-user", motherName, setMotherName)}
          </div>
          <div className="profile-form-row">
            {renderDropdown("gender", profileGenderOptions, gender, setGender)}
            {renderField(
              "profession",
              "fa-briefcase",
              profession,
              setProfession
            )}
          </div>
          <div className="profile-form-row">
            {renderDropdown(
              "marital_status",
              profileMaritalOptions,
              maritalStatus,
              setMaritalStatus
            )}
            {renderField(
              "place_of_birth",
              "fa-location-dot",
              placeOfBirth,
              setPlaceOfBirth
            )}
          </div>
          {maritalStatus?.value === 1 && (
            <div className="profile-form-row">
              {renderField(
                "spouse_full_name",
                "fa-user",
                spouseName,
                setSpouseName
              )}
              {renderField(
                "spouse_profession",
                "fa-briefcase",
                spouseProfession,
                setSpouseProfession
              )}
            </div>
          )}
          <div className="profile-form-row">
            {renderField("birth_date", "fa-calendar-days", birthDate, setBirthDate, {
              type: "date",
            })}
          </div>
          <div className="profile-checkbox-row">
            <input
              type="checkbox"
              id="pep-checkbox-profile"
              checked={isPep}
              onChange={(e) => {
                if (isEditMode && canEdit) setIsPep(e.target.checked);
              }}
              disabled={!isEditMode || !canEdit || saving}
            />
            <label htmlFor="pep-checkbox-profile">
              {t("pep_question", { ns: "mobile" })}
            </label>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddressSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("address_information", { ns: "mobile" }), "address")}
      {expandedSections.address && (
        <div className="profile-form">
          <div className="profile-doc-tabs profile-doc-tabs--address-mode" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={addressEntryMode === "details"}
              className={`profile-doc-tab ${addressEntryMode === "details" ? "profile-doc-tab--active" : ""}`}
              onClick={() => setAddressEntryMode("details")}
              disabled={saving}
            >
              <i className="fa-solid fa-pen"></i>
              <span>{t("enter_address_details", { ns: "mobile" })}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={addressEntryMode === "proof"}
              className={`profile-doc-tab ${addressEntryMode === "proof" ? "profile-doc-tab--active" : ""}`}
              onClick={() => setAddressEntryMode("proof")}
              disabled={saving}
            >
              <i className="fa-solid fa-file-arrow-up"></i>
              <span>{t("upload_proof_of_address", { ns: "mobile" })}</span>
            </button>
          </div>
          <p className="profile-address-mode-note">
            {addressEntryMode === "proof"
              ? t("address_proof_note", { ns: "mobile" })
              : t("address_details_note", { ns: "mobile" })}
          </p>
          {addressEntryMode === "details" && (
            <>
              <div className="profile-form-row" style={{ flexDirection: "column" }}>
                {renderField("state", "fa-location-crosshairs", state, setState)}
                {renderField("city", "fa-city", city, setCity)}
              </div>
              <div className="profile-form-row">
                {renderField("zip_code", "fa-hashtag", zipCode, setZipCode, { type: "number" })}
                {renderField("neighborhood", "fa-map", neighborhood, setNeighborhood)}
              </div>
              <div className="profile-form-row">
                {renderField("street", "fa-road", street, setStreet)}
                {renderField("building_name", "fa-building", buildingName, setBuildingName)}
              </div>
              <div className="profile-form-row">
                {renderField("beside_to", "fa-location-dot", besideTo, setBesideTo)}
                {renderField("floor", "fa-layer-group", floor, setFloor, { type: "number" })}
              </div>
              <div className="profile-form-row">
                {renderField("block", "fa-border-all", block, setBlock)}
                {renderField("building_number", "fa-hashtag", buildingNumber, setBuildingNumber)}
              </div>
              <div className="profile-form-row">
                {renderField("street_number", "fa-hashtag", streetNumber, setStreetNumber)}
                {renderField("landline_phone", "fa-phone", landlinePhone, setLandlinePhone, { type: "number" })}
              </div>
              <div className="profile-form-row">
                {renderField("box_number", "fa-box", boxNumber, setBoxNumber)}
                {renderField("work_address", "fa-building", workAddress, setWorkAddress)}
              </div>
              <div className="profile-form-row">
                {renderDropdown("resident_status", residenceOptions, residence, setResidence)}
              </div>
              <div className="profile-form-row">
                <div className="profile-form-group">
                  <label className="profile-form-label">{t("country", { ns: "mobile" })}</label>
                  <CustomDropdown
                    options={countryOptions}
                    value={
                      country
                        ? {
                            value: country,
                            label: countryOptions.find((o) => o.value === country)?.label || country,
                          }
                        : null
                    }
                    onChange={(v) => {
                      if (isEditMode && canEdit) setCountry(v ? v.value : "");
                    }}
                    placeholder={t("country", { ns: "mobile" })}
                    searchable={true}
                    className="profile-dropdown"
                    disabled={!isEditMode || !canEdit || saving}
                  />
                </div>
                <div className="profile-form-group">
                  <label className="profile-form-label">{t("nationality", { ns: "mobile" })}</label>
                  <CustomDropdown
                    options={countryOptions}
                    value={
                      nationality
                        ? {
                            value: nationality,
                            label: countryOptions.find((o) => o.value === nationality)?.label || nationality,
                          }
                        : null
                    }
                    onChange={(v) => {
                      if (isEditMode && canEdit) setNationality(v ? v.value : "");
                    }}
                    placeholder={t("nationality", { ns: "mobile" })}
                    searchable={true}
                    className="profile-dropdown"
                    disabled={!isEditMode || !canEdit || saving}
                  />
                </div>
              </div>
            </>
          )}
          {addressEntryMode === "proof" && (
            <>
              <h3 className="profile-section-title profile-section-title--sub">
                {t("address_proof_heading", { ns: "mobile" })}
              </h3>
              <div className="profile-form-row">
                {renderDropdown(
                  "address_document_type",
                  addressDocTypeOptions,
                  addressDocType,
                  (val) => {
                    setAddressDocType(val);
                    setAddressFile(null);
                    setDeletedDocs((prev) => ({ ...prev, address: true }));
                  }
                )}
              </div>
              <div className="profile-form-row profile-form-row--uploads profile-form-row--center">
                {renderDocImage(
                  t("address_document", { ns: "mobile" }),
                  "address",
                  addressFile,
                  setAddressFile,
                  "addressFile"
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const docTabs = [
    { key: "selfie", labelKey: "selfie", icon: "fa-camera" },
    { key: "identity", labelKey: "identity", icon: "fa-id-card" },
    { key: "signature", labelKey: "signature", icon: "fa-signature" },
  ];

  const renderDocumentsSection = () => (
    <div className="profile-section">
      {renderSectionHeader(t("documents", { ns: "mobile" }), "documents")}
      {expandedSections.documents && (
        <div className="profile-form">
          <div className="profile-doc-tabs">
            {docTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`profile-doc-tab${activeDocTab === tab.key ? " profile-doc-tab--active" : ""}`}
                onClick={() => setActiveDocTab(tab.key)}
              >
                <i className={`fa-solid ${tab.icon}`}></i>
                <span>{t(tab.labelKey, { ns: "mobile" })}</span>
              </button>
            ))}
          </div>

          <div className="profile-doc-tab-content">
            {/* Selfie */}
            {activeDocTab === "selfie" && (
              <div className="profile-form-row profile-form-row--uploads profile-form-row--center">
                {renderDocImage(t("selfie", { ns: "mobile" }), "selfie", selfieFile, setSelfieFile, "selfieFile")}
              </div>
            )}

            {/* Identity Document */}
            {activeDocTab === "identity" && (
              <>
                <div className="profile-form-row">
                  {renderDropdown(
                    "document_type",
                    identityDocTypeOptions,
                    identityDocType,
                    (val) => {
                      setIdentityDocType(val);
                      if (ONE_FACE_DOC_TYPES.includes(val?.value)) {
                        setBackIdFile(null);
                      }
                      setFrontIdFile(null);
                      setDeletedDocs((prev) => ({
                        ...prev,
                        frontId: true,
                        backId: true,
                      }));
                    }
                  )}
                  {renderField(
                    "identification_number",
                    "fa-hashtag",
                    identificationNumber,
                    setIdentificationNumber,
                    { type: "number" }
                  )}
                </div>
                <div className="profile-form-row profile-form-row--uploads">
                  {renderDocImage(
                    ONE_FACE_DOC_TYPES.includes(identityDocType?.value)
                      ? t("main_page", { ns: "mobile" })
                      : t("front_face", { ns: "mobile" }),
                    "frontId",
                    frontIdFile,
                    setFrontIdFile,
                    "frontIdFile"
                  )}
                  {!ONE_FACE_DOC_TYPES.includes(identityDocType?.value) &&
                    renderDocImage(
                      t("back_face", { ns: "mobile" }),
                      "backId",
                      backIdFile,
                      setBackIdFile,
                      "backIdFile"
                    )}
                </div>
              </>
            )}

            {/* Signature */}
            {activeDocTab === "signature" && (
              <>
                {!showSignaturePad && (
                  <div className="profile-form-row profile-form-row--uploads profile-form-row--center">
                    <div className="profile-upload-area">
                      <label className="profile-form-label">{t("signature", { ns: "mobile" })}</label>
                      <div
                        className="profile-upload-box"
                        onClick={() => {
                          if (isEditMode && canEdit) setShowSignaturePad(true);
                        }}
                        style={{
                          cursor: isEditMode && canEdit ? "pointer" : "default",
                        }}
                      >
                        {resolvedImages.signature && !deletedDocs.signature ? (
                          <div className="profile-upload-preview">
                            <img
                              src={resolvedImages.signature}
                              alt="Signature"
                              className="profile-upload-thumb"
                            />
                          </div>
                        ) : (
                          <div className="profile-upload-placeholder">
                            <i className="fa-solid fa-signature"></i>
                            <span>
                              {isEditMode && canEdit
                                ? t("click_to_draw_signature", { ns: "mobile" })
                                : t("no_signature", { ns: "mobile" })}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {showSignaturePad && (
                  <div className="profile-signature-block">
                    <div className="profile-signature-canvas-wrapper">
                      <SignatureCanvas
                        ref={signatureCanvasRef}
                        penColor="black"
                        canvasProps={{
                          className: "profile-signature-canvas",
                          width: 500,
                          height: 200,
                        }}
                      />
                    </div>
                    <div className="profile-signature-actions">
                      <button
                        type="button"
                        className="profile-sig-btn profile-sig-btn--undo"
                        onClick={() => undoSignatureStroke(signatureCanvasRef)}
                      >
                        <i className="fa-solid fa-rotate-left"></i> {t("undo", { ns: "mobile" })}
                      </button>
                      <button
                        type="button"
                        className="profile-sig-btn profile-sig-btn--clear"
                        onClick={() => signatureCanvasRef.current?.clear()}
                      >
                        <i className="fa-solid fa-eraser"></i> {t("clear", { ns: "mobile" })}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ---------- RENDER ----------
  const displayName =
    [firstName, lastName].filter(Boolean).join(" ").trim() || nickName || "User";

  return (
    <div className="profile-information-page">
      <Sidebar user={user} />
      <div className="profile-information-content">
        {error && (
          <div className="profile-error-message">
            <strong>{t("error", { ns: "mobile" })}:</strong> {error}
          </div>
        )}
        {success && (
          <div className="profile-success-message">
            <strong>{t("success", { ns: "mobile" })}:</strong> {success}
          </div>
        )}
        {loading && (
          <div className="profile-loading-message">
            {t("loading_profile_information", { ns: "mobile" })}
          </div>
        )}

        {!loading && profileData && (
          <div className="profile-information-container profile-information-container--plain" style={{ position: "relative" }}>
            {!loading && Boolean(allowEditing) && Boolean(canEdit) && !isEditMode && (
              <button
                type="button"
                className="profile-edit-btn profile-edit-btn--card"
                onClick={() => setIsEditMode(true)}
              >
                <i className="fa-solid fa-pen-to-square"></i>
                {t("edit", { ns: "mobile" })}
              </button>
            )}
            {isEditMode && (
              <button
                type="button"
                className="profile-cancel-edit-btn profile-cancel-edit-btn--card"
                onClick={() => {
                  setIsEditMode(false);
                  setShowSignaturePad(false);
                  setSelfieFile(null);
                  setFrontIdFile(null);
                  setBackIdFile(null);
                  setAddressFile(null);
                  setSignatureVideoFile(null);
                  setDeletedDocs({});
                  if (profileData) populateFields(profileData);
                }}
              >
                <i className="fa-solid fa-xmark"></i>
                {t("cancel", { ns: "mobile" })}
              </button>
            )}
            {/* Profile Header Widget */}
            <div className="profile-avatar-section">
              <div className="profile-avatar-circle">
                {resolvedImages.profile ? (
                  <img
                    src={resolvedImages.profile}
                    alt="Profile"
                    className="profile-avatar-img"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="profile-avatar-fallback"
                  style={{
                    display: resolvedImages.profile ? "none" : "flex",
                  }}
                >
                  <i className="fa-solid fa-user"></i>
                </div>
              </div>
              <div className="profile-avatar-info">
                <span className="profile-avatar-name">
                  {displayName}
                </span>
                <span
                  className={`profile-avatar-status ${
                    kycStatusText === "Verified"
                      ? "profile-avatar-status--verified"
                      : "profile-avatar-status--unverified"
                  }`}
                >
                  <i
                    className={`fa-solid ${
                      kycStatusText === "Verified"
                        ? "fa-circle-check"
                        : "fa-circle-exclamation"
                    }`}
                  ></i>
                  {kycStatusText}
                </span>
              </div>
            </div>

            {/* KYC Comments */}
            {kycStatus !== KycStatus.VERIFIED && kycComments && (
              <div className="profile-kyc-comments">
                {kycComments}
              </div>
            )}

            {/* Desktop horizontal section switcher */}
            {!isMobileView && (
              <div className="profile-sections-swiper" role="tablist" aria-label="Profile sections">
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "client" ? "active" : ""}`}
                  onClick={() => setActiveSection("client")}
                >
                  {t("client_information", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "personal" ? "active" : ""}`}
                  onClick={() => setActiveSection("personal")}
                >
                  {t("personal_information", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "address" ? "active" : ""}`}
                  onClick={() => setActiveSection("address")}
                >
                  {t("address_information", { ns: "mobile" })}
                </button>
                <button
                  type="button"
                  className={`profile-sections-swiper-tab ${activeSection === "documents" ? "active" : ""}`}
                  onClick={() => setActiveSection("documents")}
                >
                  {t("documents", { ns: "mobile" })}
                </button>
              </div>
            )}

            {/* Sections: mobile keeps collapsible, desktop shows active tab content */}
            {isMobileView ? (
              <>
                {renderClientSection()}
                {renderPersonalSection()}
                {renderAddressSection()}
                {renderDocumentsSection()}
              </>
            ) : (
              <>
                {activeSection === "client" && renderClientSection()}
                {activeSection === "personal" && renderPersonalSection()}
                {activeSection === "address" && renderAddressSection()}
                {activeSection === "documents" && renderDocumentsSection()}
              </>
            )}

            {/* Save Button */}
            {isEditMode && (
              <div className="profile-nav-buttons profile-nav-buttons--single">
                <button
                  className="profile-btn-save"
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                >
                  {saving ? (
                    t("saving", { ns: "mobile" }) + "..."
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i>
                      {t("save_changes", { ns: "mobile" })}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
